"use client";

import { GetPrices, Plan } from "@/app/actions/payments/actions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { FormatCurrency } from "@/lib/utils/ numbers";
import { Currency } from "@/types/models";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { Flex, Text } from "@radix-ui/themes";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

type PricingSwitchProps = {
  onSwitch: (value: string) => void;
};

type PricingCardProps = {
  isYearly?: boolean;
  title: string;
  monthlyPrice?: number;
  yearlyPrice?: number;
  description: string;
  features: string[];
  actionLabel: string;
  popular?: boolean;
  exclusive?: boolean;
  currency: CurrencyObject;
  action: () => void;
};

const PricingHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <section className="text-center">
    <h2 className="text-3xl font-bold">{title}</h2>
    <p className="text-xl pt-1">{subtitle}</p>
    <br />
  </section>
);

const PricingSwitch = ({ onSwitch }: PricingSwitchProps) => (
  <Tabs
    defaultValue="0"
    className="mx-auto max-w-32 h-11"
    onValueChange={onSwitch}
  >
    <TabsList className="h-11">
      <TabsTrigger value="0" className="text-base">
        Monthly
      </TabsTrigger>
      <TabsTrigger value="1" className="text-base">
        Yearly
      </TabsTrigger>
    </TabsList>
  </Tabs>
);

const PricingCard = ({
  isYearly,
  title,
  monthlyPrice,
  yearlyPrice,
  description,
  features,
  actionLabel,
  popular,
  exclusive,
  currency,
  action,
}: PricingCardProps) => (
  <Card
    className={cn(
      `w-72 flex flex-col justify-between py-1  ${popular ? "border-rose-400" : "border-zinc-700"} mx-auto sm:mx-0`,
      {
        "animate-background-shine bg-white dark:bg-[linear-gradient(110deg,#000103,45%,#1e2631,55%,#000103)] bg-[length:200%_100%] transition-colors":
          exclusive,
      },
    )}
  >
    <div>
      <CardHeader className="pb-8 pt-4">
        {isYearly && yearlyPrice && monthlyPrice ? (
          <div className="flex justify-between">
            <CardTitle className="text-zinc-700 dark:text-zinc-300 text-lg">
              {title}
            </CardTitle>
            <div
              className={cn(
                "px-2.5 rounded-xl h-fit text-sm py-1 bg-zinc-200 text-black dark:bg-zinc-800 dark:text-white",
                {
                  "bg-gradient-to-r from-orange-400 to-rose-400 dark:text-black ":
                    popular,
                },
              )}
            >
              Save{" "}
              {FormatCurrency({
                number: monthlyPrice * 12 - yearlyPrice,
                currency: currency.value as Currency,
              })}
            </div>
          </div>
        ) : (
          <CardTitle className="text-zinc-700 dark:text-zinc-300 text-lg">
            {title}
          </CardTitle>
        )}
        <div className="flex gap-0.5">
          <h3 className="text-3xl font-bold">
            {yearlyPrice && isYearly
              ? currency.label + yearlyPrice
              : monthlyPrice
                ? currency.label + monthlyPrice
                : "Free"}
          </h3>
          <span className="flex flex-col justify-end text-sm mb-1">
            {yearlyPrice && isYearly ? "/year" : monthlyPrice ? "/month" : null}
          </span>
        </div>
        <CardDescription className="pt-1.5 h-12">{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {features.map((feature: string) => (
          <CheckItem key={feature} text={feature} />
        ))}
      </CardContent>
    </div>
    <CardFooter className="mt-2">
      <Button
        onClick={action}
        className="relative inline-flex w-full items-center justify-center rounded-md bg-black text-white dark:bg-white px-6 font-medium  dark:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50"
      >
        <div className="absolute -inset-0.5 -z-10 rounded-lg bg-gradient-to-b from-[#c7d2fe] to-[#8678f9] opacity-75 blur" />
        {actionLabel}
      </Button>
    </CardFooter>
  </Card>
);

const CheckItem = ({ text }: { text: string }) => (
  <div className="flex gap-2">
    <CheckCircle2 size={18} className="my-auto text-green-400" />
    <p className="pt-0.5 text-zinc-700 dark:text-zinc-300 text-sm">{text}</p>
  </div>
);

type CurrencyObject = {
  value: string;
  label: string;
};

const currencies: CurrencyObject[] = [
  {
    value: "gbp",
    label: "£",
  },
  {
    value: "usd",
    label: "$",
  },
  {
    value: "eur",
    label: "€",
  },
  {
    value: "cad",
    label: "CA$",
  },
  {
    value: "nzd",
    label: "NZ$",
  },
  {
    value: "aud",
    label: "AU$",
  },
];

const plans = [
  {
    title: "Basic",
    price: "Free",
    description: "Ideal for solo contractors",
    features: ["1 active client", "1 active contract"],
    actionLabel: "Get Started",
  },
  {
    title: "Pro",
    monthlyPrice: 2,
    yearlyPrice: 20,
    description: "Perfect for small businesses",
    features: ["4 active clients", "4 active contracts", "Premium support"],
    actionLabel: "Get Started",
    popular: true,
  },
  {
    title: "Max",
    monthlyPrice: 4,
    yearlyPrice: 40,
    description: "The ultimate in flexibility and control",
    features: [
      "Unlimited active clients",
      "Unlimited active contracts",
      "Premium support",
    ],
    actionLabel: "Get Started",
    exclusive: true,
  },
];

export default function Page() {
  const router = useRouter();
  const [isYearly, setIsYearly] = useState(false);
  const togglePricingPeriod = (value: string) =>
    setIsYearly(parseInt(value) === 1);

  const [currency, setCurrency] = useState<CurrencyObject>(currencies[0]);
  const [isLoading, setIsLoading] = useState(false);

  const [subPlans, setSubPlans] = useState<Plan[]>([]);

  const getPlanByName = useCallback(
    (name: string) => {
      return subPlans.find((plan) => plan.name === name);
    },
    [subPlans],
  );

  const getPrices = useCallback(async () => {
    console.log(plans);

    const { data, error } = await GetPrices();
    if (error || !data) {
      console.log(error);
      return { error: "Failed to get prices" };
    }

    console.log(data);

    return { data };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    getPrices().then(({ data, error }) => {
      if (error || !data) {
        console.log(error);
        return;
      }

      setSubPlans(data);
      setIsLoading(false);
    });
  }, [getPrices]);

  const pricedPlans = useMemo(() => {
    const processedPlans = plans.map((plan) => {
      const subPlan = getPlanByName(plan.title) as Plan;
      return {
        ...plan,
        monthlyPrice:
          Number(subPlan?.currencies[currency.value]?.monthly_price) / 100,
        yearlyPrice:
          Number(subPlan?.currencies[currency.value]?.yearly_amount) / 100,
      };
    });
    return processedPlans;
  }, [subPlans, currency, getPlanByName]);

  const handleGetStarted = (plan: string) => {
    console.log(plan);
    router.push(`/signup?plan=${plan.toLowerCase()}`);
  };

  return (
    <div className="py-8 pt-24">
      <PricingHeader
        title="Pricing Plans"
        subtitle="Choose the plan that's right for you"
      />

      <PricingSwitch onSwitch={togglePricingPeriod} />
      <Flex direction="row" justify="start" align="center" gap="3">
        <Text>Currency</Text>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex justify-center" asChild>
            <Button
              variant="outline"
              className="min-w-[60px]"
              disabled={isLoading}
            >
              {!isLoading && currency.label}
              <span className="sr-only">Currency selector</span>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[60px]">
            {currencies.map((cur) => (
              <DropdownMenuItem key={cur.value}>
                <Button
                  variant="ghost"
                  className="flex justify-center p-1 text-center w-full"
                  onClick={() => setCurrency(cur)}
                >
                  {cur.label}
                </Button>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </Flex>
      <section className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-8 mt-8">
        {pricedPlans.map((plan) => (
          <PricingCard
            key={plan.title}
            {...plan}
            isYearly={isYearly}
            currency={currency}
            action={() => handleGetStarted(plan.title)}
          />
        ))}
      </section>
    </div>
  );
}
